import {
    Button,
    DrawerBody,
    DrawerFooter,
    get,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    useFormContext
} from '@missionlabs/react'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormatToNumberE164 } from 'shared/hooks/useFormatToNumberE164'
import { useGetUsersQuery } from 'shared/store'

import { AddEditTeamDetails } from './AddEditTeamDetails'
import { AddEditTeamMembers } from './AddEditTeamMembers'
import { AddEditTeamFields } from './addEditTeamSchema'

export interface AddEditTeamFormProps {
    canDelete?: boolean
    onClose: () => void
    onDelete: () => void
    onSubmit: (data: AddEditTeamFields) => Promise<void>
}

export const AddEditTeamForm: FC<AddEditTeamFormProps> = ({
    canDelete = false,
    onClose,
    onDelete,
    onSubmit
}) => {
    const { t } = useTranslation()
    const { formatToNumberE164 } = useFormatToNumberE164()

    const formRef = useRef<HTMLFormElement>(null)

    const [index, setIndex] = useState<number>(0)

    const { data: users = [] } = useGetUsersQuery()

    const methods = useFormContext<AddEditTeamFields>()
    const {
        formState: { errors, isDirty, isSubmitting },
        watch,
        setValue
    } = methods

    const hasErrors = Object.keys(errors).length > 0

    const { waitMusic, holdMusic, customWaitMusicURL, customHoldMusicURL } = useMemo(
        () => watch(),
        [watch]
    )
    const { customGreetingNoMessageURL, customGreetingURL } = useMemo(
        () => watch('voicemail'),
        [watch]
    )
    const { customGreetingNoMessageURL: busyNoMessageURL, customGreetingURL: busyURL } = useMemo(
        () => watch('busyVoicemail') || {},
        [watch]
    )

    const handleSubmit = useCallback(
        async (data: AddEditTeamFields) => {
            /** Had issues trying to modify `data` as a workaround it creates a new object with all the properties from `data` */
            const mutableData = JSON.parse(JSON.stringify(data))

            if (mutableData.voicemail.customGreetingURL === '') {
                mutableData.voicemail.greeting = 'default'
            }

            if (mutableData.voicemail.customGreetingNoMessageURL === '') {
                mutableData.voicemail.greetingNoMessage = 'default'
            }

            if (mutableData.busyVoicemail.customGreetingURL === '') {
                mutableData.busyVoicemail.greeting = 'default'
            }

            if (mutableData.busyVoicemail.customGreetingNoMessageURL === '') {
                mutableData.busyVoicemail.greetingNoMessage = 'default'
            }

            const numberE164 = mutableData.unansweredRedirect?.numberE164
            if (numberE164) {
                mutableData.unansweredRedirect!.numberE164 = formatToNumberE164(numberE164)
            }

            await onSubmit(mutableData)
        },
        [formatToNumberE164, onSubmit]
    )

    useEffect(
        function setDefaultValuesForTeamOnSwitch() {
            if (waitMusic && !customWaitMusicURL) {
                setValue('customWaitMusicURL', '')
            }

            if (holdMusic && !customHoldMusicURL) {
                setValue('customHoldMusicURL', '')
            }

            // Schema complains if greeting is custom with an empty url value
            setValue('busyVoicemail.greeting', busyURL ? 'custom' : 'default')
            setValue('busyVoicemail.greetingNoMessage', busyNoMessageURL ? 'custom' : 'default')
            setValue('voicemail.greeting', customGreetingURL ? 'custom' : 'default')
            setValue(
                'voicemail.greetingNoMessage',
                customGreetingNoMessageURL ? 'custom' : 'default'
            )
        },
        [
            waitMusic,
            customWaitMusicURL,
            holdMusic,
            customHoldMusicURL,
            customGreetingNoMessageURL,
            customGreetingURL,
            busyNoMessageURL,
            busyURL,
            setValue
        ]
    )

    useEffect(() => {
        const count = Object.keys(errors).length
        if (count === 1 && get(errors, 'callRoute.teamMembers')) setIndex(1)
        else if (count > 0) setIndex(0)
    }, [errors])

    return (
        <>
            <DrawerBody>
                <form
                    ref={formRef}
                    id="add-edit-team-form"
                    onSubmit={methods.handleSubmit(handleSubmit)}
                >
                    <Tabs size="md" index={index} isFitted onChange={setIndex}>
                        <TabList mx="24px">
                            <Tab isDisabled={isSubmitting}>{t('admin.teams.form.details_tab')}</Tab>
                            <Tab isDisabled={isSubmitting}>
                                {t('admin.teams.form.members_tab', {
                                    count: watch('callRoute.teamMembers').length
                                })}
                            </Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel p={0}>
                                <AddEditTeamDetails />
                            </TabPanel>
                            <TabPanel p={0}>
                                <AddEditTeamMembers users={users} />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </form>
            </DrawerBody>

            <DrawerFooter p="32px 24px 24px" gap="16px">
                {canDelete && (
                    <Button
                        w="full"
                        size="lg"
                        variant="negatory-outline"
                        isDisabled={isSubmitting}
                        onClick={onDelete}
                    >
                        {t('Remove')}
                    </Button>
                )}
                <Button
                    w="full"
                    size="lg"
                    variant="secondary"
                    isDisabled={isSubmitting}
                    onClick={onClose}
                >
                    {t('Cancel')}
                </Button>
                <Button
                    w="full"
                    size="lg"
                    variant="creationary"
                    isDisabled={!isDirty || hasErrors}
                    isLoading={isSubmitting}
                    type="submit"
                    form="add-edit-team-form"
                >
                    {t('Save')}
                </Button>
            </DrawerFooter>
        </>
    )
}
